/**
 * Analytics Embed Configuration and Types
 *
 * This module contains all type definitions and configuration for analytics embeds.
 * It provides a centralized mapping between URL paths, embed API keys, and
 * Amplitude tracking keys.
 */

/**
 * Configuration for analytics embeds.
 * This is the single source of truth for mapping between:
 * 1. URL path segments (urlPath)
 * 2. Embed API keys (embedType)
 * 3. Amplitude analytics tracking keys (amplitudeKey)
 *
 * Keys use namespacing to prevent collisions:
 * - Top-level tabs: 'overview', 'classes', etc.
 * - Namespaced tabs: 'teachers:engagement', 'students:engagement', etc.
 *
 * @example
 * // URL: /analytics/overview
 * // Config key: 'overview'
 * // Maps to: { embedType: 'overview', amplitudeKey: 'overview' }
 *
 * @example
 * // URL: /analytics/teachers/engagement
 * // Config key: 'teachers:engagement'
 * // Maps to: { embedType: 'teachers_engagement', amplitudeKey: 'teachers' }
 */
// NOTE: embedType values below are a backend API contract.
// Do not rename without coordinating with the backend service team.
export const ANALYTICS_EMBED_CONFIG = {
  // Top-level analytics tabs
  organisation: {
    embedType: 'organisation',
    amplitudeKey: 'organisation',
  },
  overview: {
    embedType: 'overview',
    amplitudeKey: 'overview',
  },
  socrative: {
    embedType: 'socrative',
    amplitudeKey: 'socrative',
  },
  'students-parents': {
    embedType: 'student_parent',
    amplitudeKey: 'students-parents',
  },
  classes: {
    embedType: 'class',
    amplitudeKey: 'classes',
  },
  schools: {
    embedType: 'school',
    amplitudeKey: 'schools',
  },

  // Organisation sub-tabs (accessed via /analytics/organisation/:name)
  'organisation:engagement': {
    embedType: 'organisation_engagement',
    amplitudeKey: 'organisation', // Track as 'organisation' in Amplitude
  },
  'organisation:instruction': {
    embedType: 'organisation_instruction',
    amplitudeKey: 'organisation-instruction',
  },
  'organisation:feedback': {
    embedType: 'organisation_feedback',
    amplitudeKey: 'organisation-feedback',
  },
  'organisation:assessments': {
    embedType: 'organisation_assessment',
    amplitudeKey: 'organisation-assessments',
  },

  // Teachers sub-tabs (accessed via /analytics/teachers/:name)
  'teachers:engagement': {
    embedType: 'teacher_engagement',
    amplitudeKey: 'teachers', // Track as 'teachers' in Amplitude
  },
  'teachers:instruction': {
    embedType: 'teacher_instruction',
    amplitudeKey: 'teachers-instruction',
  },
  'teachers:feedback': {
    embedType: 'teacher_feedback',
    amplitudeKey: 'teachers-feedback',
  },
  'teachers:assessments': {
    embedType: 'teacher_assessment',
    amplitudeKey: 'teachers-assessments',
  },

  // Future: Students sub-tabs would go here as 'students:engagement', etc.
} as const;

/**
 * URL path segments used in analytics routes.
 * These are the values that appear in the URL (e.g., /analytics/:urlPath)
 */
export type AnalyticsEmbedUrlPath = keyof typeof ANALYTICS_EMBED_CONFIG;

/**
 * Embed types used by the GraphQL API.
 * These are the values sent to the backend to fetch a specific embed.
 */
export type AnalyticsEmbedType =
  | (typeof ANALYTICS_EMBED_CONFIG)[AnalyticsEmbedUrlPath]['embedType']
  | 'district_overview'; // Special case for district purchaser type

/**
 * Amplitude tracking keys for analytics tabs.
 * These are the values sent to Amplitude for metric tracking.
 */
export type AnalyticsAmplitudeKey =
  (typeof ANALYTICS_EMBED_CONFIG)[AnalyticsEmbedUrlPath]['amplitudeKey'];

/**
 * Helper function to get embed type from URL path.
 * Handles the special case for district overview and namespace resolution.
 *
 * @param urlPath - The URL path segment (e.g., 'engagement', 'overview')
 * @param namespace - Optional namespace (e.g., 'teachers', 'students')
 * @param purchaserType - Optional purchaser type for special cases
 * @returns The embed type to use
 *
 * @example
 * getAnalyticsEmbedType('engagement', 'teachers') // 'teachers_engagement'
 * getAnalyticsEmbedType('overview') // 'overview'
 * getAnalyticsEmbedType('overview', undefined, 'District') // 'district_overview'
 */
export function getAnalyticsEmbedType(
  urlPath: string,
  namespace?: string,
  purchaserType?: string,
): AnalyticsEmbedType {
  // Special case: district purchasers use a different overview embed
  if (urlPath === 'overview' && purchaserType === 'District') {
    return 'district_overview';
  }

  // Build the config key with namespace if provided
  const configKey = namespace
    ? (`${namespace}:${urlPath}` as AnalyticsEmbedUrlPath)
    : (urlPath as AnalyticsEmbedUrlPath);

  const config = ANALYTICS_EMBED_CONFIG[configKey];
  if (!config) {
    throw new Error(
      `Invalid analytics config key: ${configKey}. Check ANALYTICS_EMBED_CONFIG.`,
    );
  }

  return config.embedType;
}

/**
 * Helper function to get Amplitude tracking key from URL path.
 *
 * @param urlPath - The URL path segment (e.g., 'engagement', 'overview')
 * @param namespace - Optional namespace (e.g., 'teachers', 'students')
 * @returns The Amplitude key to use for tracking
 *
 * @example
 * getAnalyticsAmplitudeKey('engagement', 'teachers') // 'teachers'
 * getAnalyticsAmplitudeKey('overview') // 'overview'
 */
export function getAnalyticsAmplitudeKey(
  urlPath: string,
  namespace?: string,
): AnalyticsAmplitudeKey {
  // Build the config key with namespace if provided
  const configKey = namespace
    ? (`${namespace}:${urlPath}` as AnalyticsEmbedUrlPath)
    : (urlPath as AnalyticsEmbedUrlPath);

  const config = ANALYTICS_EMBED_CONFIG[configKey];
  if (!config) {
    throw new Error(
      `Invalid analytics config key: ${configKey}. Check ANALYTICS_EMBED_CONFIG.`,
    );
  }

  return config.amplitudeKey;
}
